import { Injectable } from '@angular/core';

/** Extensions readable as plain text straight from the file. */
const TEXT_EXTENSIONS = new Set([
  'txt', 'md', 'markdown', 'csv', 'tsv', 'json', 'log',
  'html', 'htm', 'xml', 'css', 'sql', 'ini', 'toml', 'yml', 'yaml',
  'js', 'mjs', 'ts', 'jsx', 'tsx', 'py', 'rb', 'go', 'rs', 'java', 'c', 'h', 'cpp', 'hpp', 'cs', 'sh', 'ps1', 'bat', 'php', 'swift', 'kt',
]);

/** Human-readable list used in error messages. */
const SUPPORTED_LABEL = 'plain text, CSV, JSON, code, .docx, and .pdf files';

@Injectable({ providedIn: 'root' })
export class FileTextService {
  /** Shared `accept` attribute value for file pickers. */
  static readonly ACCEPT = [
    ...[...TEXT_EXTENSIONS].map((ext) => `.${ext}`),
    'text/plain',
    '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.pdf,application/pdf',
  ].join(',');

  static readonly SUPPORTED_LABEL = SUPPORTED_LABEL;

  /**
   * Extracts readable text from a common file type. Returns null when the file
   * cannot be read as text (unsupported or binary). `maxChars` optionally caps
   * the returned length so callers that embed the text in a prompt stay bounded.
   */
  async extractText(file: File, maxChars?: number): Promise<string | null> {
    const ext = this.extension(file.name);
    let text: string;
    try {
      if (ext === 'docx') {
        // Dynamically import mammoth to avoid CommonJS bundling issues.
        const { extractRawText } = await import('mammoth');
        const result = await extractRawText({ arrayBuffer: await file.arrayBuffer() });
        text = result.value;
      } else if (ext === 'pdf') {
        text = await this.extractPdf(file);
      } else if (TEXT_EXTENSIONS.has(ext) || file.type.startsWith('text/') || file.type === 'application/json') {
        text = await file.text();
      } else {
        return null;
      }
    } catch (error) {
      console.error(`Failed to extract text from ${file.name}:`, error);
      return null;
    }
    if (maxChars && text.length > maxChars) {
      text = `${text.slice(0, maxChars)}\n\n[…content truncated]`;
    }
    return text;
  }

  private async extractPdf(file: File): Promise<string> {
    // Dynamically import pdfjs-dist to avoid CommonJS bundling issues.
    const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
    if (!GlobalWorkerOptions.workerSrc) {
      GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.js';
    }
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await getDocument({ data: arrayBuffer }).promise;
    let textContent = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const pageText = content.items
        .map((item) => ('str' in item ? item.str : ''))
        .join(' ');
      textContent += pageText + '\n';
    }
    return textContent;
  }

  private extension(name: string): string {
    const index = name.lastIndexOf('.');
    return index >= 0 ? name.slice(index + 1).toLowerCase() : '';
  }
}
